import { z } from "zod";
import { hasControlChars } from "../security/sanitize.js";
/**
 * The «Sitio web» configuration (docs/plans/ai-first-site.md, Decisions 3–4):
 * ONE JSON document per site, validated here and nowhere else — it is the
 * contract between the CRM editor, the backend and apps/site.
 *
 * Only what nothing else needs lives here. The business name, logo, colour,
 * greeting, WhatsApp number, map link and the whole catalog are read live from
 * where the tenant already keeps them, never copied in: a copy would drift and
 * the page would contradict the agent.
 *
 * Every field has a default, so `siteConfigSchema.parse({})` is a complete,
 * publishable configuration — the draft a new site starts from — and a section
 * added in a later `schemaVersion` reads as its default on an older document.
 * Images are STORAGE PATHS in the public branding bucket, never URLs: the
 * backend resolves them, so a bucket move is not a migration of every config.
 */
export const SITE_CONFIG_SCHEMA_VERSION = 1;
/**
 * The hero bar's text when `hero.inputPlaceholder` is empty. One constant: apps/site
 * renders the bar on the server and the widget replaces it, so the two must agree
 * or the text visibly changes as the page loads.
 */
export const DEFAULT_HERO_PLACEHOLDER = "¿En qué te ayudamos?";
/** Plain text a tenant types: trimmed, capped, no control characters. */
function siteText(max) {
    return z
        .string()
        .trim()
        .max(max)
        .refine((v) => !hasControlChars(v), "Contiene caracteres no permitidos");
}
/** A path under `sites/<clientId>/` in the branding bucket; the service checks the prefix. */
const sitePathSchema = z
    .string()
    .max(500)
    .regex(/^sites\/[0-9a-f-]{36}\/[A-Za-z0-9._/-]+$/, "Ruta de imagen inválida")
    .refine((v) => !v.split("/").includes(".."), "Ruta de imagen inválida");
const httpUrlSchema = z
    .string()
    .trim()
    .max(500)
    .url()
    .refine((v) => /^https?:\/\//i.test(v), "Debe ser un enlace http(s)");
const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hora inválida (HH:MM)");
const dayHoursSchema = z
    .object({ open: z.boolean(), from: timeOfDaySchema, to: timeOfDaySchema })
    .refine((d) => !d.open || d.from < d.to, "La hora de cierre debe ser después de la de apertura");
/**
 * The site's look, chosen per client from fixed presets rather than free
 * controls (docs/plans/site-redesign.md): a preset is something we designed and
 * checked; a colour picker per element is a site nobody designed. Every field is
 * defaulted, so a configuration saved before the block existed parses to the
 * defaults without a `schemaVersion` bump.
 */
export const SITE_HERO_STYLES = ["dark", "light"];
export const SITE_CATALOG_LAYOUTS = ["sections", "mosaic", "rows", "list"];
export const SITE_TYPOGRAPHIES = ["industrial", "artisanal", "modern"];
/** `contain`: catalog shots of a part on white, shown whole. `cover`: photos of the real thing, cropped to fill. */
export const SITE_PHOTO_FITS = ["contain", "cover"];
export const siteConfigSchema = z.object({
    schemaVersion: z.literal(SITE_CONFIG_SCHEMA_VERSION).default(SITE_CONFIG_SCHEMA_VERSION),
    style: z
        .object({
        hero: z.enum(SITE_HERO_STYLES).default("dark"),
        catalogLayout: z.enum(SITE_CATALOG_LAYOUTS).default("sections"),
        typography: z.enum(SITE_TYPOGRAPHIES).default("industrial"),
        photoFit: z.enum(SITE_PHOTO_FITS).default("contain"),
    })
        .default({}),
    seo: z
        .object({ title: siteText(70).default(""), description: siteText(160).default("") })
        .default({}),
    // Always rendered: it holds the agent. No `enabled`.
    hero: z
        .object({
        headline: siteText(80).default(""),
        subheadline: siteText(160).default(""),
        inputPlaceholder: siteText(60).default(""),
        imagePath: sitePathSchema.nullable().default(null),
    })
        .default({}),
    trust: z
        .object({
        enabled: z.boolean().default(false),
        title: siteText(60).default(""),
        items: z
            .array(z.object({
            name: siteText(60).pipe(z.string().min(1)),
            logoPath: sitePathSchema.nullable().default(null),
            url: httpUrlSchema.nullable().default(null),
        }))
            .max(8)
            .default([]),
        figures: z
            .array(z.object({ value: siteText(20).pipe(z.string().min(1)), label: siteText(40).pipe(z.string().min(1)) }))
            .max(4)
            .default([]),
    })
        .default({}),
    catalog: z
        .object({ enabled: z.boolean().default(true), title: siteText(60).default("") })
        .default({}),
    about: z
        .object({
        enabled: z.boolean().default(false),
        title: siteText(60).default(""),
        // Markdown. apps/site must render it WITHOUT raw HTML (react-markdown's
        // default); a tenant's text is never trusted markup.
        body: siteText(1500).default(""),
        imagePath: sitePathSchema.nullable().default(null),
    })
        .default({}),
    testimonials: z
        .object({
        // Off by default and never pre-filled: the page shows only real ones.
        enabled: z.boolean().default(false),
        items: z
            .array(z.object({
            quote: siteText(300).pipe(z.string().min(1)),
            author: siteText(80).pipe(z.string().min(1)),
            role: siteText(80).nullable().default(null),
            logoPath: sitePathSchema.nullable().default(null),
        }))
            .max(6)
            .default([]),
    })
        .default({}),
    location: z
        .object({
        enabled: z.boolean().default(true),
        address: siteText(200).default(""),
        // Monday..Sunday when set. Structured, not free text, so the agent can read
        // it too; null hides the hours rather than inventing a schedule.
        hours: z.array(dayHoursSchema).length(7).nullable().default(null),
        email: z.string().trim().max(254).email().nullable().default(null),
    })
        .default({}),
    footer: z.object({ showCredit: z.boolean().default(true) }).default({}),
});
/** `PUT /super-admin/clients/:id/site/hostnames`. Normalized and validated by the service. */
export const siteHostnamesSchema = z.object({
    hostnames: z.array(z.string().trim().min(1).max(260)).max(10),
});
/**
 * What the page's buttons ask the agent to do, from the close the agent can
 * actually perform (`resolvePrimaryAction` in the backend's site service): a page
 * must never offer a visit, a quote or an order the agent cannot take. `ask` is
 * the neutral fallback for every other tenant.
 */
export const SITE_PRIMARY_ACTIONS = ["quote", "order", "visit", "ask"];
// ----- Measurement (phase 4) -----
/**
 * What the AI-first site counts (Decision 6). `product_view` is a `page_view` on
 * `/productos/*`; "started a conversation" is the lead itself, which the site's
 * inline chat creates on the first message.
 */
export const SITE_EVENT_TYPES = ["page_view", "cta_click", "chip_click"];
/** A label from the host page or the widget: untrusted, short, never sent to a model. */
const siteEventEntrySchema = z
    .string()
    .max(120)
    .refine((v) => !hasControlChars(v), "entry contains disallowed characters")
    .transform((v) => v.normalize("NFKC"));
export const siteEventSchema = z.object({
    type: z.enum(SITE_EVENT_TYPES),
    /** `location.pathname`; the server drops the query and anything that is not a path. */
    path: z.string().min(1).max(2000),
    /** The visit's first page_view only: the full landing URL (its UTM tags) and the external referrer. */
    landingUrl: z.string().url().max(2000).optional(),
    referrer: z.string().url().max(2000).optional(),
    /** cta_click / chip_click: the button's label. */
    entry: siteEventEntrySchema.optional(),
});
/**
 * `POST /public/site-events` — sent by the widget from the tenant's own page.
 * Batched, small: a page view is one event, a click another.
 */
export const siteEventsRequestSchema = z.object({
    clientId: z.string().uuid(),
    visitorId: z.string().uuid(),
    events: z.array(siteEventSchema).min(1).max(10),
});
//# sourceMappingURL=site.js.map